import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Modal,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import MessageGroupCarousel from "../Chat/MessageGroupCarousel";
import axios from "axios";

const EvalResults = () => {
  // State for Message Evals (Tab 0)
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modelNames, setModelNames] = useState([]);
  const [modelOrder, setModelOrder] = useState([]);
  const [selectedBetter, setSelectedBetter] = useState("");
  const [scores, setScores] = useState({});
  const [showScores, setShowScores] = useState(false);
  const [choices, setChoices] = useState({});

  // State for Intent Options Evals (Tab 1)
  const [intentOptionsResults, setIntentOptionsResults] = useState({});
  const [intentOptionsLoading, setIntentOptionsLoading] = useState(true);
  const [currentIntentOptionsIndex, setCurrentIntentOptionsIndex] = useState(0);
  const [intentOptionsExpanded, setIntentOptionsExpanded] = useState({});
  const [intentRunNames, setIntentRunNames] = useState([]);
  const [intentRunOrder, setIntentRunOrder] = useState([]); // Holds the two runs currently being displayed, in their possibly shuffled order
  const [selectedIntentBetter, setSelectedIntentBetter] = useState(""); // "Run 1" or "Run 2"
  const [intentScores, setIntentScores] = useState({}); // { runName: score }
  const [showIntentScoresModal, setShowIntentScoresModal] = useState(false);
  const [intentChoices, setIntentChoices] = useState({}); // { index: { choice: "Run 1", runOrder: [actualRunName1, actualRunName2] } }

  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Fetch eval results for Messages
  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await axios.get("/view-eval-results/");
        setResults(response.data);
        setModelNames(Object.keys(response.data));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching eval results:", error);
        setLoading(false);
      }
    };

    if (currentTab === 0) {
      fetchResults();
    }
  }, [currentTab]);

  // Fetch eval results for Intent Options
  useEffect(() => {
    const fetchIntentOptionsResults = async () => {
      setIntentOptionsLoading(true);
      try {
        const response = await axios.get(
          "/view-reply-user-intent-options-eval-results/"
        );
        setIntentOptionsResults(response.data || {});
        const runNames = Object.keys(response.data || {});
        setIntentRunNames(runNames);
        // If the first run has no data, treat as empty
        if (
          runNames.length > 0 &&
          (!response.data[runNames[0]] ||
            response.data[runNames[0]].length === 0)
        ) {
          // This case means runs exist but are empty, effectively no results to show from them.
          // setIntentRunNames([]); // Or handle display of "empty runs"
        }
        setIntentOptionsLoading(false);
      } catch (error) {
        console.error("Error fetching intent options eval results:", error);
        setIntentOptionsResults({});
        setIntentRunNames([]);
        setIntentOptionsLoading(false);
      }
    };

    if (currentTab === 1) {
      fetchIntentOptionsResults();
    }
  }, [currentTab]);

  // Update selected choice when changing examples for Message Evals
  useEffect(() => {
    if (currentTab === 0) {
      setSelectedBetter(choices[currentIndex] || "");
    }
  }, [currentIndex, choices, currentTab]);

  // Update selected intent choice when changing items for Intent Options Evals
  useEffect(() => {
    if (currentTab === 1) {
      setSelectedIntentBetter(
        intentChoices[currentIntentOptionsIndex]?.choice || ""
      );
    }
  }, [currentIntentOptionsIndex, intentChoices, currentTab]);

  // Randomize model order when currentIndex changes for Message Evals
  useEffect(() => {
    if (currentTab === 0 && modelNames.length) {
      if (!choices[currentIndex]) {
        const shuffled = [...modelNames].sort(() => Math.random() - 0.5);
        setModelOrder(shuffled);
      } else {
        setModelOrder(choices[currentIndex].modelOrder);
      }
    }
  }, [currentIndex, modelNames, choices, currentTab]);

  // Effect to set and shuffle intent run order for comparison (Tab 1)
  useEffect(() => {
    if (currentTab === 1 && intentRunNames.length > 0) {
      // If a choice has already been made for this item, use the stored order
      if (intentChoices[currentIntentOptionsIndex]?.runOrder) {
        setIntentRunOrder(intentChoices[currentIntentOptionsIndex].runOrder);
      } else {
        // Otherwise, shuffle the first two available runs for a new blind comparison
        const availableRuns = [...intentRunNames];
        if (availableRuns.length >= 2) {
          const twoRuns = [availableRuns[0], availableRuns[1]];
          // Shuffle them
          setIntentRunOrder(twoRuns.sort(() => Math.random() - 0.5));
        } else {
          setIntentRunOrder(availableRuns); // Use the single run, or empty if no runs
        }
      }
    } else if (currentTab === 1) {
      setIntentRunOrder([]); // Clear if no runs or not on tab 1
    }
  }, [currentIntentOptionsIndex, intentRunNames, currentTab, intentChoices]); // Added intentChoices dependency

  // Handlers for Message Evals
  const handleNext = () => {
    const totalResults = Object.values(results)[0]?.length || 0;
    setCurrentIndex((prev) => Math.min(prev + 1, totalResults - 1));
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleVote = (event) => {
    const better = event.target.value; // "Model 1" or "Model 2"
    const actualModelVotedFor = modelOrder[better === "Model 1" ? 0 : 1];
    const previousChoiceForThisItem = choices[currentIndex];

    setScores((prevScores) => {
      const newScores = { ...prevScores };
      // If there was a previous vote for this item, decrement the score of the previously chosen model
      if (previousChoiceForThisItem) {
        const previousActualModel =
          modelOrder[previousChoiceForThisItem.choice === "Model 1" ? 0 : 1];
        newScores[previousActualModel] =
          (newScores[previousActualModel] || 1) - 1;
        if (newScores[previousActualModel] === 0) {
          delete newScores[previousActualModel];
        }
      }
      // Increment score for the newly voted model
      newScores[actualModelVotedFor] =
        (newScores[actualModelVotedFor] || 0) + 1;
      return newScores;
    });

    setChoices((prevChoices) => ({
      ...prevChoices,
      [currentIndex]: { choice: better, modelOrder: [...modelOrder] }, // Store a copy of modelOrder
    }));
    setSelectedBetter(better);
  };

  // Handlers for Intent Options Evals
  const handleNextIntentOptions = () => {
    const firstRunName = intentRunOrder[0]; // Use the first run in the current order
    if (!firstRunName || !intentOptionsResults[firstRunName]) {
      return;
    }
    const totalItems = intentOptionsResults[firstRunName]?.length || 0;
    setCurrentIntentOptionsIndex((prev) => Math.min(prev + 1, totalItems - 1));
  };

  const handlePreviousIntentOptions = () => {
    setCurrentIntentOptionsIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleIntentVote = (event) => {
    const betterRunLabel = event.target.value; // "Run 1" or "Run 2"
    if (intentRunOrder.length < 2) return; // Should not happen if radio buttons are visible

    const actualRunVotedFor =
      intentRunOrder[betterRunLabel === "Run 1" ? 0 : 1];
    const previousChoiceForThisItem = intentChoices[currentIntentOptionsIndex];

    setIntentScores((prevScores) => {
      const newScores = { ...prevScores };
      // If there was a previous vote, decrement the score of the previously chosen run
      if (previousChoiceForThisItem) {
        const previousActualRun =
          intentRunOrder[previousChoiceForThisItem.choice === "Run 1" ? 0 : 1];
        newScores[previousActualRun] = (newScores[previousActualRun] || 1) - 1;
        if (newScores[previousActualRun] === 0) {
          delete newScores[previousActualRun];
        }
      }
      // Increment score for the newly voted run
      newScores[actualRunVotedFor] = (newScores[actualRunVotedFor] || 0) + 1;
      return newScores;
    });

    setIntentChoices((prevChoices) => ({
      ...prevChoices,
      [currentIntentOptionsIndex]: {
        choice: betterRunLabel,
        runOrder: [...intentRunOrder],
      },
    }));
    setSelectedIntentBetter(betterRunLabel);
  };

  const toggleIntentOptionsExpanded = (index) => {
    setIntentOptionsExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const renderMessageEvals = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 120px)"
        >
          <CircularProgress />
        </Box>
      );
    }
    const hasResults =
      modelNames.length > 0 && Object.values(results)[0]?.length > 0;
    if (!hasResults) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 120px)"
        >
          <Typography>No message evaluation results available</Typography>
        </Box>
      );
    }
    const firstModel = modelNames[0];
    const currentResult = results[firstModel]?.[currentIndex];
    if (!currentResult) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 120px)"
        >
          <Typography>Loading result...</Typography>{" "}
          <CircularProgress size={20} />
        </Box>
      );
    }
    const totalResults = results[firstModel]?.length || 0;

    return (
      <>
        {/* Navigation Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Group for arrows and title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              sx={{ color: "#FFFFFF" }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ color: "#FFFFFF" }}>
              Result {currentIndex + 1} of {totalResults}
            </Typography>
            <IconButton
              onClick={handleNext}
              disabled={currentIndex === totalResults - 1}
              sx={{ color: "#FFFFFF" }}
            >
              <ArrowForward />
            </IconButton>
          </Box>

          {/* View Scores Button */}
          <Button
            variant="contained"
            onClick={() => setShowScores(true)}
            size="small"
          >
            View Scores
          </Button>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flex: 1,
            overflow: "hidden",
            bgcolor: "#000000",
          }}
        >
          {/* Images Section */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              alignContent: "flex-start",
              gap: 2,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "#1C1C1E" },
              "&::-webkit-scrollbar-thumb": {
                background: "#38383A",
                borderRadius: "4px",
              },
            }}
          >
            {currentResult.images?.map((imageUrl, index) => (
              <img
                key={index}
                src={imageUrl}
                alt={`Activity ${currentResult.activity_id} - Image ${
                  index + 1
                }`}
                style={{
                  maxWidth:
                    currentResult.images?.length === 1
                      ? "100%"
                      : "calc(50% - 8px)",
                  objectFit: "contain",
                  height: "auto",
                  borderRadius: "8px",
                  border: "1px solid #38383A",
                }}
              />
            ))}
          </Box>

          {/* Message Groups Section */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { background: "#1C1C1E" },
              "&::-webkit-scrollbar-thumb": {
                background: "#38383A",
                borderRadius: "4px",
              },
            }}
          >
            {modelOrder.map((modelName, idx) => (
              <Box key={modelName}>
                <Typography variant="h6" sx={{ mb: 1, color: "#FFFFFF" }}>
                  Model {idx + 1}
                </Typography>
                {results[modelName]?.[currentIndex]?.success ? (
                  <MessageGroupCarousel
                    messageId={`eval-${results[modelName][currentIndex].activity_id}-${modelName}`}
                    messageGroups={
                      results[modelName][currentIndex].message_groups
                    }
                    metadata={{
                      activity_type: "reply",
                      more_suggestions: false,
                    }}
                    onCopy={async (text) => {
                      await navigator.clipboard.writeText(text);
                      return true;
                    }}
                  />
                ) : (
                  <Typography color="error">
                    Error: {results[modelName]?.[currentIndex]?.error}
                  </Typography>
                )}
              </Box>
            ))}
            <Box
              sx={{
                mt: 3,
                mb: 5,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <RadioGroup
                value={choices[currentIndex]?.choice || ""}
                onChange={handleVote}
              >
                <FormControlLabel
                  value="Model 1"
                  control={<Radio sx={{ color: "#FFFFFF" }} />}
                  label="Model 1 is better"
                  sx={{ color: "#FFFFFF" }}
                />
                <FormControlLabel
                  value="Model 2"
                  control={<Radio sx={{ color: "#FFFFFF" }} />}
                  label="Model 2 is better"
                  sx={{ color: "#FFFFFF" }}
                />
              </RadioGroup>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  const renderIntentOptionsEvals = () => {
    if (intentOptionsLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 120px)"
        >
          <CircularProgress />
        </Box>
      );
    }

    const noResultsToShow =
      intentRunOrder.length === 0 ||
      !intentOptionsResults[intentRunOrder[0]] ||
      intentOptionsResults[intentRunOrder[0]].length === 0;

    if (noResultsToShow) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 120px)"
        >
          <Typography>
            No Intent Options evaluation results available for comparison.
          </Typography>
        </Box>
      );
    }

    const firstRunNameInOrder = intentRunOrder[0];
    const representativeItem =
      intentOptionsResults[firstRunNameInOrder]?.[currentIntentOptionsIndex];

    if (!representativeItem) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="calc(100vh - 120px)"
        >
          <Typography>Loading result item...</Typography>{" "}
          <CircularProgress size={20} />
        </Box>
      );
    }
    const totalItems = intentOptionsResults[firstRunNameInOrder]?.length || 0;

    return (
      <>
        {/* Navigation Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between", // Changed for View Scores button
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={handlePreviousIntentOptions}
              disabled={currentIntentOptionsIndex === 0}
              sx={{ color: "#FFFFFF" }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ color: "#FFFFFF" }}>
              Item {currentIntentOptionsIndex + 1} of {totalItems}
            </Typography>
            <IconButton
              onClick={handleNextIntentOptions}
              disabled={currentIntentOptionsIndex === totalItems - 1}
              sx={{ color: "#FFFFFF" }}
            >
              <ArrowForward />
            </IconButton>
          </Box>
          {intentRunOrder.length > 0 && ( // Show button only if there are runs to score
            <Button
              variant="contained"
              onClick={() => setShowIntentScoresModal(true)}
              size="small"
            >
              View Scores
            </Button>
          )}
        </Box>

        {/* Main Content: Images | Run1 Details | Run2 Details (if applicable) */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flex: 1,
            overflow: "hidden",
            bgcolor: "#000000",
          }}
        >
          {/* Images Section (Common for the activity_id) */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              alignContent: "flex-start",
              gap: 2,
              // ... scrollbar styles ...
            }}
          >
            {representativeItem.images?.map((imageUrl, index) => (
              <img
                key={index}
                src={imageUrl}
                alt={`Activity ${representativeItem.activity_id} - Image ${
                  index + 1
                }`}
                style={{
                  maxWidth:
                    representativeItem.images?.length === 1
                      ? "100%"
                      : "calc(50% - 8px)",
                  objectFit: "contain",
                  height: "auto",
                  borderRadius: "8px",
                  border: "1px solid #38383A",
                }}
              />
            ))}
          </Box>

          {/* Intent Options Details Section (for each run in intentRunOrder) */}
          <Box
            sx={{
              flex: intentRunOrder.length > 1 ? 2 : 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column", // Changed to column to stack vote below runs
              gap: 2,
              padding: "10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                flexGrow: 1,
              }}
            >
              {" "}
              {/* Inner box for side-by-side runs */}
              {intentRunOrder.map((runName, idx) => {
                // runName here is the actual name, but we only use idx for label
                const currentRunItem =
                  intentOptionsResults[runName]?.[currentIntentOptionsIndex];
                if (!currentRunItem) {
                  return (
                    <Box key={runName} sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: "#FFFFFF" }}>
                        Run {idx + 1}
                      </Typography>
                      <Typography color="error">
                        Data not available for this item.
                      </Typography>
                    </Box>
                  );
                }
                const isExpanded =
                  intentOptionsExpanded[currentIntentOptionsIndex];
                const optionsToShow = isExpanded
                  ? currentRunItem.user_intent_options
                  : currentRunItem.first_three_user_intent_options;

                return (
                  <Box
                    key={runName}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#FFFFFF", mb: 1 }}>
                      Run {idx + 1} {/* Display "Run 1" or "Run 2" */}
                    </Typography>
                    {currentRunItem.success ||
                    currentRunItem.success === undefined ? (
                      <>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#B0B0B0",
                            fontStyle: "italic",
                            fontSize: "0.9rem",
                          }}
                        >
                          Remark: {currentRunItem.remark || "N/A"}
                        </Typography>
                        <List
                          dense
                          sx={{
                            bgcolor: "#1C1C1E",
                            borderRadius: 1,
                            p: 1,
                            flexGrow: 1,
                            overflowY: "auto",
                          }}
                        >
                          {optionsToShow?.map((option, optIdx) => (
                            <ListItem
                              key={optIdx}
                              sx={{
                                borderBottom: "1px solid #38383A",
                                "&:last-child": { borderBottom: "none" },
                              }}
                            >
                              <ListItemText
                                primary={`${optIdx + 1}. ${option}`}
                                sx={{
                                  color: "#E0E0E0",
                                  "& .MuiTypography-root": {
                                    fontSize: "1rem",
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                          {!optionsToShow ||
                            (optionsToShow.length === 0 && (
                              <ListItem>
                                <ListItemText
                                  primary="No intent options."
                                  sx={{ color: "#888" }}
                                />
                              </ListItem>
                            ))}
                        </List>
                        {currentRunItem.user_intent_options &&
                          currentRunItem.user_intent_options.length >
                            (currentRunItem.first_three_user_intent_options
                              ?.length || 0) &&
                          currentRunItem.user_intent_options.length > 3 && ( // Only show if more than 3 total
                            <Button
                              onClick={() =>
                                toggleIntentOptionsExpanded(
                                  currentIntentOptionsIndex
                                )
                              }
                              variant="outlined"
                              size="small"
                              sx={{
                                color: "#FFFFFF",
                                borderColor: "#FFFFFF",
                                alignSelf: "flex-start",
                                mt: 1,
                              }}
                              startIcon={
                                isExpanded ? <ExpandLess /> : <ExpandMore />
                              }
                            >
                              {isExpanded
                                ? "Show Less"
                                : `Show All ${currentRunItem.user_intent_options.length} Options`}
                            </Button>
                          )}
                      </>
                    ) : (
                      <Typography color="error">
                        Error:{" "}
                        {currentRunItem.error || "Marked as unsuccessful"}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>{" "}
            {/* End of Inner box for side-by-side runs */}
            {/* Voting Radio Buttons for Intent Options - only if 2 runs are shown */}
            {intentRunOrder.length === 2 && (
              <Box
                sx={{
                  mt: 3,
                  mb: 2, // Adjusted margin
                  display: "flex",
                  flexDirection: "column",
                  gap: 1, // Adjusted gap
                  alignSelf: "center", // Center the radio group
                  width: "fit-content",
                }}
              >
                <RadioGroup
                  value={selectedIntentBetter}
                  onChange={handleIntentVote}
                  row // Display radio buttons in a row
                >
                  <FormControlLabel
                    value="Run 1" // Corresponds to intentRunOrder[0]
                    control={<Radio sx={{ color: "#FFFFFF" }} />}
                    label="Run 1 is better"
                    sx={{ color: "#FFFFFF" }}
                  />
                  <FormControlLabel
                    value="Run 2" // Corresponds to intentRunOrder[1]
                    control={<Radio sx={{ color: "#FFFFFF" }} />}
                    label="Run 2 is better"
                    sx={{ color: "#FFFFFF" }}
                  />
                </RadioGroup>
              </Box>
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box
      sx={{
        p: 3,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#000000",
        color: "#FFFFFF",
      }}
    >
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        centered
        sx={{
          mb: 2,
          "& .MuiTabs-indicator": { backgroundColor: "#FFFFFF" },
          "& .MuiTab-root": { color: "#B0B0B0" },
          "& .Mui-selected": { color: "#FFFFFF!important" },
        }}
      >
        <Tab label="Message Evals" />
        <Tab label="Intent Options Evals" />
      </Tabs>

      {currentTab === 0 && renderMessageEvals()}
      {currentTab === 1 && renderIntentOptionsEvals()}

      {/* Scores Modal (only for Message Evals) */}
      {currentTab === 0 && (
        <Modal
          open={showScores}
          onClose={() => setShowScores(false)}
          aria-labelledby="scores-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "#1C1C1E",
              border: "1px solid #38383A",
              borderRadius: 2,
              p: 4,
              color: "#FFFFFF",
              minWidth: 300,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Model Scores
            </Typography>
            {Object.entries(scores).map(([model, score]) => (
              <Typography key={model}>
                {model}: {score} votes
              </Typography>
            ))}
          </Box>
        </Modal>
      )}

      {/* Scores Modal for Intent Options Evals */}
      {currentTab === 1 && (
        <Modal
          open={showIntentScoresModal}
          onClose={() => setShowIntentScoresModal(false)}
          aria-labelledby="intent-scores-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "#1C1C1E",
              border: "1px solid #38383A",
              borderRadius: 2,
              p: 4,
              color: "#FFFFFF",
              minWidth: 300,
              boxShadow: 24, // For better visibility
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2 }}
              id="intent-scores-modal-title"
            >
              Intent Options Run Scores
            </Typography>
            {Object.keys(intentScores).length > 0 ? (
              Object.entries(intentScores).map(([runName, score]) => (
                <Typography key={runName}>
                  {runName}: {score} vote{score === 1 ? "" : "s"}
                </Typography>
              ))
            ) : (
              <Typography>No votes recorded yet.</Typography>
            )}
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default EvalResults;
